import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ROUTES } from '@/shared/config/routes';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useDebounce } from '@/shared/hooks';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { SESSIONS_QUERY_KEY_ROOT } from '@/shared/constants/query-keys.constants';
import { getChatSessions } from '../../api/sessions.api';
import { SessionsTable } from '../SessionsTable';
import { SessionsToolbar } from '../SessionsToolbar';
import { SessionsViewState, resolveSessionsViewState } from '../SessionsViewState';

export interface SessionsContentProps {
  /**
   * dependency injection — `closeSidePanel` from `@/app/store/ui` lifted to consuming
   * page (`pages/Sessions`). Keeps `features/sessions` cross-app-OUT clean.
   */
  closeSidePanel: () => void;
}

export function SessionsContent({ closeSidePanel }: SessionsContentProps) {
  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId();

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const [rowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: [SESSIONS_QUERY_KEY_ROOT, rowsPerPage, page, debouncedSearchInput, workspaceId],
    queryFn: () =>
      getChatSessions({
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        take: rowsPerPage,
        skip: rowsPerPage * page,
        search: debouncedSearchInput,
        workspaceId: workspaceId!,
      }),
    enabled: !!workspaceId,
  });

  const rows = query.data?.contexts ?? [];
  const totalCount = query.data?.total ?? 0;

  const navigateToNewSession = () => void navigate(ROUTES.TOOLS_HUB);

  const navigateToSession = (id: string) => {
    closeSidePanel();
    void navigate(ROUTES.CHAT_SESSION(id));
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setPage(0);
  };

  const viewState = resolveSessionsViewState({
    isLoading: query.isLoading,
    isError: query.isError,
    rows,
    hasSearch: !!debouncedSearchInput,
    onNewSession: navigateToNewSession,
    onClearFilters: handleClearFilters,
  });

  const showToolbar = viewState.type === 'data' || viewState.type === 'no-results';

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showToolbar && (
        <SessionsToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          onNewSession={navigateToNewSession}
        />
      )}

      {viewState.type === 'data' ? (
        <SessionsTable
          rows={rows}
          isLoading={query.isFetching}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onRowClick={navigateToSession}
          onPageChange={handlePageChange}
        />
      ) : (
        <SessionsViewState state={viewState} />
      )}
    </div>
  );
}
